import { downloadFileTrigger } from '@dealstack/frontend/shared/files/downloadFile';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2';
import { I18Namespaces } from '@dealstack/shared/lib/common/i18n/i18n';
import { formatUnicorn } from '@dealstack/shared/lib/format/formatUnicorn';
import { documentLibraryControllerBulkDownloadDocumentFilesMutation } from '@dealstack/shared/lib/backendClient/@tanstack/react-query.gen';
import API_PATHS from '@dealstack/shared/lib/generated/apiPaths';
import { Button } from '@mantine/core';
import { useMutation } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { TranslateFunction } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.types';
import type { BulkDownloadVariables } from './useBulkDocumentDownload.types';

export const useBulkDocumentDownload = ({
	dealId,
	tDocuments,
}: {
	dealId: string;
	tDocuments: TranslateFunction<I18Namespaces.DOCUMENT_LIBRARY>;
}) => {
	const navigate = useNavigate();
	const mutation = useMutation({
		...documentLibraryControllerBulkDownloadDocumentFilesMutation(),
		mutationKey: [
			formatUnicorn(API_PATHS.DEAL_DEALID_DOCUMENT_LIBRARY_BULK_DOWNLOAD_POST, {
				dealId,
			}),
		],
		// `data` is the generated union (sync pre-signed URL | async-job marker);
		// narrow on the discriminating key.
		onSuccess: (data) => {
			if ('isAsync' in data && data.isAsync) {
				showToastV2({
					title: tDocuments('modals.bulk-download.title'),
					state: 'success',
					message: (
						<div>
							<p>{tDocuments('modals.bulk-download.async-message')}</p>
							<Button
								variant="white"
								onClick={() => navigate('/exports', { replace: true })}
							>
								{tDocuments('modals.bulk-download.go-button')}
							</Button>
						</div>
					),
				});
				return;
			}

			if ('downloadUrl' in data && data.downloadUrl) {
				downloadFileTrigger(data.downloadUrl);
			}
		},
	});

	return {
		mutate: ({
			dealId: targetDealId,
			allDocumentsSelected,
			documentIds,
			filters,
		}: BulkDownloadVariables) => {
			const excludedDocumentIds = allDocumentsSelected ? documentIds : undefined;
			const selectedDocumentIds = allDocumentsSelected ? undefined : documentIds;

			mutation.mutate({
				path: { dealId: targetDealId },
				body: {
					allDocumentsSelected,
					excludedDocumentIds,
					selectedDocumentIds,
					filters,
				},
			});
		},
		isPending: mutation.isPending,
	};
};
